import type { LazyQueryExecFunction } from '@apollo/client';
import { createContext, useCallback, useContext, useState } from 'react';
import type { MessageDescriptor } from 'react-intl';

import type { SearchRefinementInput } from '__generated__/globalTypes';
import { FilterTitle } from 'components/search/FilterTitle';
import type { PrintablePlayerSortName } from 'contexts/config';

type Refinement = SearchRefinementInput;
type RefinementType = 'string' | 'numeric';

type GQLCtx = {
  query: string;
  refineQuery: (newQuery: string) => void;
  activeFacets: {
    [attribute: string]: string[];
  };
  search: LazyQueryExecFunction<any, any>;
  refine(attribute: string, cb: (prevValues: string[]) => string[]): void;
  refinements: Refinement[];
  advancedFilters: string;
  onSaleOnly: boolean;
  page: number;
  pages: number;
  facets: {
    [field: string]: {
      values: Array<{
        count: number;
        value: string;
      }>;
      max?: { value: string };
      min?: { value: string };
    };
  };
  sorts: PrintablePlayerSortName[];
  getRefinementType: (v: string) => RefinementType;
  getRefinementApiAttribute: (v: string) => string;
};

export const GQLSearchContext = createContext<GQLCtx | null>(null);

export const useGQLSearchContext = () => {
  return useContext(GQLSearchContext);
};

const toggle = (values: string[]) => (prev: string[]) => {
  const out = [...prev];
  for (const value of values) {
    const index = out.indexOf(value);
    if (index === -1) {
      out.push(value);
    } else {
      out.splice(index, 1);
    }
  }
  return out;
};

export const useFacet = (attribute: string) => {
  const ctx = useContext(GQLSearchContext)!;

  const value = ctx.activeFacets[attribute] ?? [];

  const apiAttribute = ctx.getRefinementApiAttribute(attribute);
  const [itemsFromSearch, setItemsFromSearch] = useState(null);

  const items = (itemsFromSearch ?? ctx.facets[attribute]?.values ?? []).map(
    e => ({
      ...e,
      label: e.value,
      isRefined: value.includes(e.value),
    })
  );

  return {
    search: (q: string) => {
      if (!q) {
        setItemsFromSearch(null);
        return;
      }
      ctx
        .search({
          variables: {
            refinements: ctx.refinements.filter(e => e.field !== apiAttribute),
            field: apiAttribute,
            facetQuery: q,
          },
        })
        .then(e => e.data.searchPlayerFacetValues)
        .then(setItemsFromSearch);
    },
    toggle,
    refine: (cb: (pv: string[]) => string[]) => {
      ctx.refine(attribute, cb);
    },
    value,
    isFromSearch: Boolean(itemsFromSearch),
    items,
  };
};

export const useBackendPagination = () => {
  const ctx = useContext(GQLSearchContext)!;

  return {
    page: ctx.page,
    pages: ctx.pages,
    refine: (page: number) => {
      if (page === 0) {
        ctx.refine('p', () => []);
      } else {
        ctx.refine('p', () => [(page + 1).toString()]);
      }
    },
  };
};

const parseNum = (input: string | undefined) => {
  if (input != null) {
    return Number.parseInt(input, 10);
  }
  return undefined;
};

const format = (value: undefined | number, defaultV: undefined | number) => {
  if (value == null || value === defaultV) {
    return '';
  }
  return value.toString();
};

export const useBackendRangeSlider = ({
  attribute,
  min: minP,
  max: maxP,
}: {
  attribute: string;
  max?: number;
  min?: number;
}) => {
  const ctx = useContext(GQLSearchContext)!;

  const refine = useCallback(
    ([newMin, newMax]: [number | undefined, number | undefined]) => {
      const min =
        minP ??
        (parseNum(ctx.facets[attribute]?.min?.value) as undefined | number);
      const max =
        maxP ??
        (parseNum(ctx.facets[attribute]?.max?.value) as undefined | number);

      if (!format(newMin, min).length && !format(newMax, max).length) {
        ctx.refine(attribute, () => []);
      } else {
        ctx.refine(attribute, () => [
          [format(newMin, min), format(newMax, max)].join(':'),
        ]);
      }
    },
    [ctx, attribute, maxP, minP]
  );

  const min =
    minP ?? (parseNum(ctx.facets[attribute]?.min?.value) as undefined | number);
  const max =
    maxP ?? (parseNum(ctx.facets[attribute]?.max?.value) as undefined | number);

  const ret = {
    refine,
    range: { min, max },
    start:
      ctx.activeFacets[attribute]?.flatMap(e =>
        e.split(':').map(ee => Number.parseInt(ee, 10))
      ) ?? [],
    canRefine: true,
  };

  return ret;
};

type TitleProps = {
  name: string;
  shortTitle?: MessageDescriptor;
  title: MessageDescriptor;
  short?: boolean;
};
export const GqlFilterTitle = ({
  name,
  shortTitle,
  title,
  short,
}: TitleProps) => {
  const { value } = useFacet(name);
  return (
    <FilterTitle
      name={(short && shortTitle) || title!}
      selected={value.length}
    />
  );
};
